import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Main from "../../_app/layouts/Main";
import { permissionCodes } from "../../permission/hooks";
import { useAutoSwitchResults } from "../hooks";
import StatusChip from "../../_app/components/StatusChip";
import { formatCurrency, formatDate } from "../../_app/utils/format";
import NA from "../../_app/components/NA";
import { AutoSwitchResult } from "../types";
import { createStylesheet } from "../../_app/utils/styles";
import { UILink, UILoader, UISkeleton } from "../../_app/components";
import UIDataTable, { UIDataTableColumnDef, UIDataTableState } from "../../_app/components/Table/UIDataTable";
import { Feature } from "../../feature/types";
import { UserType } from "../../user-level/types";

export const SimSwitchResults = () => {
  const classes = useStyles();
  const rowCountOptions = [25, 50, 100];
  const [page, setPage] = useState(0);
  const [rowCount, setRowCount] = useState(rowCountOptions[0]);
  const [isComplete, setIsComplete] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const { data = { content: [], number: page, totalElements: 0, size: rowCount } } = useAutoSwitchResults(
    location.state?.requestId,
    page,
    rowCount,
    {
      refetchInterval: !isComplete ? 2000 : false,
    },
  );

  const hasExistingCode = (item: AutoSwitchResult) => {
    return item.status === "FAILED" && item.reason === "There is an existing code for this number";
  };

  const columns: UIDataTableColumnDef[] = [
    "Number",
    {
      name: `${data.content?.[0]?.autoSwitchType || ""} Code`,
      options: {
        display: data.content?.[0]?.autoSwitchType !== "ETF" ? "true" : "false",
      },
    },
    "Expiry Date",
    "Early Termination Fee",
    "Status",
    {
      name: "Reason",
      options: {
        display: data.content.find(hasExistingCode) ? "true" : "false",
      },
    },
  ];
  const placeholder = (item: AutoSwitchResult) => {
    return item.status === "PENDING" ? <UISkeleton /> : <NA />;
  };
  const parsed =
    data.content.map((item) => [
      item.cli || placeholder(item),
      item.code || placeholder(item),
      formatDate(item.expiryDate, "dd/MM/yyyy") || placeholder(item),
      item.earlyTermination !== null ? formatCurrency(item.earlyTermination) : placeholder(item),
      <StatusChip
        status={item.status || undefined}
        {...(item.status === "FAILED" ? { href: "/sim-disconnect", component: "a", clickable: true } : {})}
      />,
      hasExistingCode(item) ? (
        <>
          There is an existing code for this number, you can find it on the{" "}
          <UILink onClick={() => navigate("/sim-disconnect")} className={classes.link}>
            disconnection
          </UILink>{" "}
          page
        </>
      ) : (
        <NA />
      ),
    ]) || [];

  useEffect(() => {
    function hasIncompleteResult() {
      return Boolean(
        data.content.find((item: AutoSwitchResult) => {
          return item.status === "PENDING";
        }),
      );
    }
    setIsComplete(!hasIncompleteResult());
  }, [data]);

  const handleTableChange = (action: string, state: UIDataTableState) => {
    switch (action) {
      case "changePage":
        setPage(state.page);
        break;
      case "changeRowsPerPage":
        setPage(0);
        setRowCount(state.rowsPerPage);
        break;
      default:
        break;
    }
  };

  return (
    <Main
      title="Auto Switch - Confirmation"
      data-cy="auto-switch-conf-page"
      feature={Feature.AutoSwitchPacStac}
      accessPermission={permissionCodes.MOBILE_MANAGEMENT}
      isLoading={!Boolean(data.content[0])}
      showNav={false}
      showToolbar={false}
      showFooter={false}
      allowedUserTypes={[UserType.ACCOUNT, UserType.COST_CENTRE]}
    >
      {!isComplete && (
        <div className={classes.loader}>
          <UILoader linear color="primary" />
        </div>
      )}
      <div data-cy="results-table" className={classes.tableCtr}>
        <UIDataTable
          title={""}
          data={parsed}
          columns={columns}
          options={{
            onTableChange: handleTableChange,
            pagination: true,
            page,
            rowsPerPage: rowCount,
            rowsPerPageOptions: rowCountOptions,
            count: data?.totalElements,
            download: false,
            elevation: 1,
            print: false,
            responsive: "standard",
            selectToolbarPlacement: "none",
            filter: false,
            sort: false,
            search: false,
            serverSide: true,
            selectableRows: "none",
            rowHover: true,
            setTableProps: () => ({ size: "small" }),
            viewColumns: false,
            setRowProps: (row) => {
              return {
                "data-cy": `row-id-${row?.[1]}`,
              };
            },
          }}
        />
      </div>
    </Main>
  );
};

const useStyles = createStylesheet((theme) => ({
  tableCtr: {
    width: "100%",
    height: "calc(100vh - 108px)",
  },
  loader: {
    [theme.breakpoints.up("sm")]: {
      width: "calc(100% - 40px)",
      position: "absolute",
      top: "120px",
      zIndex: 101,
    },
    width: "100%",
  },
  link: {
    cursor: "pointer",
  },
}));

export default SimSwitchResults;
